import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import * as Yup from 'yup'
import { Formik } from 'formik'
import { useTheme } from '@mui/material/styles'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import OutlinedInput from '@mui/material/OutlinedInput'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { useFormik } from "formik";
import AES from 'crypto-js/aes';
import Utf8 from 'crypto-js/enc-utf8';
import {
    DASHBOARD_PAGE,
    FORGOT_PAGE,
    SIGNUP_PAGE,
    ACCESS_CODE_PAGE,
} from '@routes/constants'
import httpClient from '@utils/httpClient'
import { SET_USER, SET_TOKEN, SET_LOADER } from '@store/actions'
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material'
let secretKey = 'sk'
const AuthLogin = ({ ...others }) => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const isMounted = useRef(true)

    const org = useSelector((state) => state.org.org ? state.org.org.data : {})
    const [rememberMe, setRememberMe] = useState(false);
    const [showError, setShowError] = useState(null)
    const [showPassword, setShowPassword] = useState(false)
    const [preFilledData, setPreFilledData] = useState({});
    const validationSchema =
        Yup.object({
            identifier: Yup.string()
                .max(255)
                .required('This field is required'),
            password: Yup.string()
                .max(255)
                .required('Password is required'),
        })


    const handleClickShowPassword = () => {
        setShowPassword(!showPassword)
    }

    const handleMouseDownPassword = (event) => {
        event.preventDefault()
    }

    const handleRememberMeChange = (event) => {
        setRememberMe(event.target.checked);
    };

    const handleLogin = async (
        values, actions
    ) => {
        try {
            setShowError(null)
            dispatch({ type: SET_LOADER, data: true })
            httpClient.post('/auth/local', { ...values }).then((res) => {
                dispatch({ type: SET_LOADER, data: false })
                if (res && res.ok) {
                    const { token, user } = res
                    dispatch({ type: SET_USER, data: user })
                    dispatch({ type: SET_TOKEN, data: token })
                    if (rememberMe) {
                        localStorage.setItem('rememberMe', 'true');
                        localStorage.setItem('identifier', AES.encrypt(values.identifier, secretKey).toString());
                        localStorage.setItem('password', AES.encrypt(values.password, secretKey).toString());
                    } else {
                        localStorage.removeItem('rememberMe');
                        localStorage.removeItem('identifier');
                        localStorage.removeItem('password');
                    }

                    if (isMounted.current) {
                        if (!!user.has_org_approval) {
                            navigate(DASHBOARD_PAGE)
                        } else {
                            navigate(ACCESS_CODE_PAGE)
                        }
                    }
                } else if (res.message) {
                    setShowError(res.message)
                }
            })
        } catch (err) {
            if (isMounted.current) {
            }
        }
    }

    const formik = useFormik({
        initialValues: {
            identifier: preFilledData.identifier || '',
            password: preFilledData.password || '',
        },
        validationSchema: validationSchema,
        onSubmit: handleLogin,
    })

    useEffect(() => {
        return () => {
            isMounted.current = false
        }
    }, [])

    function handleDecrypt(encryptedValue, secretKey) {
        const bytes = AES.decrypt(encryptedValue, secretKey);
        const originalValue = bytes.toString(Utf8);
        return originalValue;
    }

    useEffect(() => {
        let rememberMe = localStorage.getItem('rememberMe');
        let savedEmail = localStorage.getItem('identifier');
        let savedPwd = localStorage.getItem('password');
        if (rememberMe) {
            setRememberMe(true);
            formik.setFieldValue("identifier", handleDecrypt(savedEmail, secretKey) || "");
            formik.setFieldValue("password", handleDecrypt(savedPwd, secretKey) || "");
        }
    }, [])

    return (
        <>
            <Grid container direction="column" justifyContent="left" sx={{ p: 2 }}>
                {showError && (
                    <Grid item xs={12} alignItems="center" justifyContent="left">
                        <Alert severity="error" sx={{ border: '1px solid #FF0000' }}>{showError}</Alert>
                    </Grid>
                )}
                <Grid item xs={12} alignItems="center" justifyContent="left" sx={{ mt: 0 }}>
                    <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                        Please login to your account.
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Box sx={{ maxWidth: '350px', margin: '0 auto', width: '100%' }}>
                        <form onSubmit={formik.handleSubmit} {...others}>
                            <FormControl
                                fullWidth
                                error={Boolean(
                                    formik.touched.identifier && formik.errors.identifier
                                )}
                                sx={{
                                    ...theme.typography.customInput, '& .MuiOutlinedInput-notchedOutline': {
                                        border: '0',
                                        boxShadow: '0px 6px 20px #1F54C326',
                                    }
                                }}
                            >
                                <InputLabel htmlFor="identifier">
                                    Email Address or Mobile
                                </InputLabel>
                                <OutlinedInput
                                    id="identifier"
                                    type="text"
                                    autoComplete={formik.values.identifier}
                                    value={formik.values.identifier}
                                    name="identifier"
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    label="Email Address or Mobile"
                                />
                                {Boolean(
                                    formik.touched.identifier && formik.errors.identifier
                                ) && (
                                        <FormHelperText error>
                                            {formik.errors.identifier}
                                        </FormHelperText>
                                    )}
                            </FormControl>
                            <FormControl
                                fullWidth
                                error={Boolean(
                                    formik.touched.password && formik.errors.password
                                )}
                                sx={{
                                    ...theme.typography.customInput,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: "0",
                                        boxShadow: '0px 6px 20px #1F54C326',
                                    }
                                }}
                            >
                                <InputLabel htmlFor="password-login">
                                    Password
                                </InputLabel>
                                <OutlinedInput
                                    id="password-login"
                                    autoComplete={formik.values.identifier}
                                    type={showPassword ? 'text' : 'password'}
                                    value={formik.values.password}
                                    name="password"
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={
                                                    handleClickShowPassword
                                                }
                                                onMouseDown={
                                                    handleMouseDownPassword
                                                }
                                                edge="end"
                                                size="large"
                                            >
                                                {showPassword ? (
                                                    <Visibility />
                                                ) : (
                                                    <VisibilityOff />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    label="Password"
                                />
                                {Boolean(
                                    formik.touched.password && formik.errors.password
                                ) && (
                                        <FormHelperText error>
                                            {formik.errors.password}
                                        </FormHelperText>
                                    )}
                            </FormControl>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="right"
                                spacing={1}
                            >
                                <FormGroup sx={{ marginRight: 'auto' }}>
                                    <FormControlLabel control={
                                        <Checkbox checked={rememberMe}
                                            onClick={handleRememberMeChange}
                                        />} label="Keep me logged in" sx={{ fontFamily: '"Poppins",sans-serif,Arial' }} />
                                </FormGroup>
                                <Link
                                    to={FORGOT_PAGE}
                                    style={{ textDecoration: 'none' }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        color="secondary"
                                        className="pointer"
                                        sx={{ textDecoration: 'none', fontFamily: '"Poppins",sans-serif,Arial', color: '#616161' }}
                                    >
                                        Login Help ?
                                    </Typography>
                                </Link>
                            </Stack>
                            {formik.errors.submit && (
                                <Box sx={{ mt: 3 }}>
                                    <FormHelperText error>
                                        {formik.errors.submit}
                                    </FormHelperText>
                                </Box>
                            )}
                            <Box sx={{ mt: 5 }}>
                                <Button
                                    disableElevation
                                    // disabled={
                                    //     formik.isSubmitting ||
                                    //     !formik.isValid
                                    // }
                                    fullWidth
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                    sx={{ fontWeight: 700, p: 1.5 }}
                                >
                                    Login
                                </Button>
                            </Box>
                        </form>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Stack
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                        spacing={1}
                        sx={{ mt: 6 }}
                    >
                        <Typography
                            variant="body1"
                            color="secondary"
                            sx={{ textDecoration: 'none', fontSize: '12px' }}
                        >
                            First time User?
                        </Typography>
                        <Link to={SIGNUP_PAGE} style={{ textDecoration: 'none' }}>
                            <Typography
                                variant="h3"
                                color="secondary"
                                className="pointer"
                                sx={{ textDecoration: 'none' }}
                            >
                                Sign Up Here
                            </Typography>
                        </Link>
                    </Stack>
                </Grid>
            </Grid >
        </>
    )
}

export default AuthLogin
